<!-- Show spinner if loading -->
@if (isLoading) {
  <div class="loading-spinner">
    <mat-spinner></mat-spinner>
  </div>
}

<!-- Show thank-you component if not loading and showThankYou is true -->
@if (!isLoading && showThankYou) {
  <app-thank-you></app-thank-you>
}

<!-- Show borrower form if not loading and showThankYou is false -->
@if (!isLoading && !showThankYou) {
  <div class="borrower-dashboard">
    <h1>Start your Borrower Application</h1>
    <!-- Main Form -->
    <form [formGroup]="borrowerForm">
      <!-- Business Details -->
      <mat-card>
        <mat-card-header>
          <mat-card-title>Business Details</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <!-- Business Legal Name -->
          <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
            <mat-label>Business Legal Name</mat-label>
            <input matInput formControlName="businessLegalName" required>
            @if (borrowerForm.get('businessLegalName')?.hasError('required')) {
              <mat-error>
                Business Legal Name is required
              </mat-error>
            }
          </mat-form-field>
          <!-- Business DBA -->
          <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
            <mat-label>Business DBA</mat-label>
            <input matInput formControlName="businessDBA">
          </mat-form-field>
          <!-- Business Email -->
          <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
            <mat-label>Business Email</mat-label>
            <input matInput formControlName="businessEmail" required>
            @if (borrowerForm.get('businessEmail')?.hasError('required')) {
              <mat-error>
                Business Email is required
              </mat-error>
            }
            @if (borrowerForm.get('businessEmail')?.hasError('email')) {
              <mat-error>
                Please enter a valid email address
              </mat-error>
            }
          </mat-form-field>
          <!-- Business Phone -->
          <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
            <mat-label>Business Phone</mat-label>
            <input matInput formControlName="businessPhone" required type="tel" mask="(000) 000-0000" maxlength="15"
              inputmode="tel" (keypress)="onlyNumberKey($event)" placeholder="Enter 10-digit phone number">
            @if (borrowerForm.get('businessPhone')?.hasError('required')) {
              <mat-error>
                Business Phone is required
              </mat-error>
            }
            @if (borrowerForm.get('businessPhone')?.hasError('pattern')) {
              <mat-error>
                Business Phone must be exactly 10 digits
              </mat-error>
            }
          </mat-form-field>
          <!-- Business Website -->
          <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
            <mat-label>Business Website</mat-label>
            <input matInput formControlName="businessWebsite">
          </mat-form-field>
          <!-- Business Structure -->
          <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
            <mat-label>Business Structure</mat-label>
            <mat-select formControlName="businessStructure" required>
              <mat-option value="SOLE_PROPRIETORSHIP">Sole Proprietorship</mat-option>
              <mat-option value="PARTNERSHIP">Partnership</mat-option>
              <mat-option value="LLC">LLC</mat-option>
              <mat-option value="CORPORATION">Corporation</mat-option>
              <mat-option value="NON_PROFIT">Non Profit</mat-option>
            </mat-select>
            @if (borrowerForm.get('businessStructure')?.hasError('required')) {
              <mat-error>
                Business Structure is required
              </mat-error>
            }
          </mat-form-field>
          <!-- Business Tax ID -->
          @if (!isSoleProprietorship) {
            <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
              <mat-label>Business Tax ID</mat-label>
              <input matInput formControlName="businessTaxID" [value]="borrowerForm.controls['businessTaxID'].value | taxId">
              @if (borrowerForm.get('businessTaxID')?.hasError('required')) {
                <mat-error>
                  Business Tax ID is required
                </mat-error>
              }
              @if (borrowerForm.get('businessTaxID')?.hasError('pattern')) {
                <mat-error>
                  Business Tax ID must be valid
                </mat-error>
              }
            </mat-form-field>
          }
          <!-- Industry Type -->
          <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
            <mat-label>Industry</mat-label>
            <mat-select formControlName="industryType" required>
              <mat-option value="ACCOUNTING">Accounting</mat-option>
              <mat-option value="CONSTRUCTION">Construction / Home Services</mat-option>
              <mat-option value="INSURANCE">Insurance Services</mat-option>
              <mat-option value="LEGAL">Legal Services</mat-option>
              <mat-option value="MANUFACTURING">Manufacturing</mat-option>
              <mat-option value="NON_PROFIT_GOVERNMENT">Non Profit / Government</mat-option>
              <mat-option value="RETAIL_TRADE">Retail</mat-option>
              <mat-option value="TECHNOLOGY_SOFTWARE">Technology / Software</mat-option>
              <mat-option value="WHOLESALE_TRADE">Wholesale</mat-option>
              <mat-option value="OTHER">Other</mat-option>
            </mat-select>
            @if (borrowerForm.get('industryType')?.hasError('required')) {
              <mat-error>
                Industry is required
              </mat-error>
            }
          </mat-form-field>
          <!-- Years in Business -->
          <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
            <mat-label>Years in Business</mat-label>
            <mat-select formControlName="yearsInBusiness" required>
              <mat-option value="LESS_THAN_1_YEAR">Less than 1 year</mat-option>
              <mat-option value="ONE_TO_TWO_YEARS">1 to 2 years</mat-option>
              <mat-option value="THREE_TO_FIVE_YEARS">3 to 5 years</mat-option>
              <mat-option value="SIX_TO_TEN_YEARS">6 to 10 years</mat-option>
              <mat-option value="TEN_TO_FIFTEEN_YEARS">10 to 15 years</mat-option>
              <mat-option value="SIXTEEN_TO_TWENTY_YEARS">16 to 20 years</mat-option>
              <mat-option value="OVER_TWENTY_YEARS">20+ years</mat-option>
            </mat-select>
            @if (borrowerForm.get('yearsInBusiness')?.hasError('required')) {
              <mat-error>
                Years in Business is required
              </mat-error>
            }
          </mat-form-field>
          <!-- Number of Employees -->
          <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
            <mat-label>Number of Employees</mat-label>
            <input matInput type="number" formControlName="numberOfEmployees" required>
            @if (borrowerForm.get('numberOfEmployees')?.hasError('required')) {
              <mat-error>
                Number of Employees is required
              </mat-error>
            }
            @if (borrowerForm.get('numberOfEmployees')?.hasError('min')) {
              <mat-error>
                Please enter a number 1 or higher
              </mat-error>
            }
          </mat-form-field>
          <!-- Gross Annual Revenue -->
          <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
            <mat-label>Gross Annual Revenue</mat-label>
            <input matInput type="number" formControlName="grossAnnualRevenue" required>
            @if (borrowerForm.get('grossAnnualRevenue')?.hasError('required')) {
              <mat-error>
                Gross Annual Revenue is required
              </mat-error>
            }
            @if (borrowerForm.get('grossAnnualRevenue')?.hasError('min')) {
              <mat-error>
                Please enter a number 1 or higher
              </mat-error>
            }
            @if (borrowerForm.get('grossAnnualRevenue')?.hasError('max')) {
              <mat-error>
                Gross Annual Revenue cannot exceed 15 digits
              </mat-error>
            }
          </mat-form-field>
          <!-- Referral Number -->
          <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
            <mat-label>Referral Number</mat-label>
            <input matInput formControlName="referralNumber">
            @if (borrowerForm.get('referralNumber')?.hasError('maxlength')) {
              <mat-error>
                Referral Number cannot exceed 15 characters
              </mat-error>
            }
          </mat-form-field>
        </mat-card-content>
      </mat-card>
      <!-- Business Address -->
      <mat-card>
        <mat-card-header>
          <mat-card-title>Business Address</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <!-- Street Address -->
          <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
            <mat-label>Street Address</mat-label>
            <input matInput formControlName="streetAddress" required>
            @if (borrowerForm.get('streetAddress')?.hasError('required')) {
              <mat-error>
                Street Address is required
              </mat-error>
            }
          </mat-form-field>
          <!-- Suite/Unit/Office -->
          <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
            <mat-label>Suite/Unit/Office</mat-label>
            <input matInput formControlName="suiteUnitOffice">
          </mat-form-field>
          <!-- City -->
          <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
            <mat-label>City</mat-label>
            <input matInput formControlName="city" required>
            @if (borrowerForm.get('city')?.hasError('required')) {
              <mat-error>
                City is required
              </mat-error>
            }
          </mat-form-field>
          <!-- State -->
          <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
            <mat-label>State</mat-label>
            <mat-select formControlName="state">
              @for (state of usStates; track state) {
                <mat-option [value]="state">{{state}}</mat-option>
              }
            </mat-select>
            @if (borrowerForm.get('state')?.hasError('required')) {
              <mat-error>
                Please enter a valid state abbreviation
              </mat-error>
            }
          </mat-form-field>
          <!-- Zip Code -->
          <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
            <mat-label>Zip Code</mat-label>
            <input matInput formControlName="zipCode" required type="text" maxlength="5" pattern="\d{5}"
              inputmode="numeric" (keypress)="onlyNumberKey($event)" placeholder="Enter 5-digit Zip Code">
            @if (borrowerForm.get('zipCode')?.hasError('required')) {
              <mat-error>
                Zip Code is required
              </mat-error>
            }
            @if (borrowerForm.get('zipCode')?.hasError('pattern')) {
              <mat-error>
                Zip Code must be exactly 5 digits
              </mat-error>
            }
          </mat-form-field>
        </mat-card-content>
      </mat-card>
      <!-- Owners Section -->
      <mat-card>
        <mat-card-header>
          <mat-card-title>Owners</mat-card-title>
          <mat-checkbox formControlName="noIndividualOver25">
            No individual directly or indirectly owns more than 25% of the company.
          </mat-checkbox>
        </mat-card-header>
        <mat-card-content>
          <div formArrayName="owners">
            <mat-accordion>
              @for (owner of owners.controls; track owner; let i = $index) {
                <mat-expansion-panel [formGroupName]="i"
                  class="owner-panel" #panel="matExpansionPanel">
                  <mat-expansion-panel-header>
                    <mat-panel-title>
                      Owner {{ i + 1 }} or Authorizing Individual
                    </mat-panel-title>
                    <mat-panel-description>
                      Click to {{ panel.expanded ? 'collapse' : 'expand' }}
                    </mat-panel-description>
                  </mat-expansion-panel-header>
                  <!-- Owner Form Fields -->
                  <div class="owner-form">
                    <!-- First Name -->
                    <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
                      <mat-label>First Name</mat-label>
                      <input matInput formControlName="firstName" required>
                      @if (owner.get('firstName')?.hasError('required')) {
                        <mat-error>
                          First Name is required
                        </mat-error>
                      }
                    </mat-form-field>
                    <!-- Middle Initial -->
                    <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
                      <mat-label>Middle Initial</mat-label>
                      <input matInput formControlName="middleInitial">
                    </mat-form-field>
                    <!-- Last Name -->
                    <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
                      <mat-label>Last Name</mat-label>
                      <input matInput formControlName="lastName" required>
                      @if (owner.get('lastName')?.hasError('required')) {
                        <mat-error>
                          Last Name is required
                        </mat-error>
                      }
                    </mat-form-field>
                    <!-- Email Address -->
                    <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
                      <mat-label>Email Address</mat-label>
                      <input matInput formControlName="emailAddress" required>
                      @if (owner.get('emailAddress')?.hasError('required')) {
                        <mat-error>
                          Email Address is required
                        </mat-error>
                      }
                      @if (owner.get('emailAddress')?.hasError('email')) {
                        <mat-error>
                          Please enter a valid email address
                        </mat-error>
                      }
                    </mat-form-field>
                    <!-- Phone Number -->
                    <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
                      <mat-label>Phone Number</mat-label>
                      <input matInput formControlName="phoneNumber" required type="tel" mask="(000) 000-0000"
                        maxlength="15" inputmode="tel" (keypress)="onlyNumberKey($event)"
                        placeholder="Enter 10-digit phone number">
                      @if (owner.get('phoneNumber')?.hasError('required')) {
                        <mat-error>
                          Phone Number is required
                        </mat-error>
                      }
                      @if (owner.get('phoneNumber')?.hasError('pattern')) {
                        <mat-error>
                          Phone Number must be exactly 10 digits
                        </mat-error>
                      }
                    </mat-form-field>
                    <!-- Date of Birth -->
                    <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
                      <mat-label>Date of Birth</mat-label>
                      <input matInput formControlName="dateOfBirth" type="date" required>
                      @if (owner.get('dateOfBirth')?.hasError('required')) {
                        <mat-error>
                          Date of Birth is required
                        </mat-error>
                      }
                    </mat-form-field>
                    <!-- SSN -->
                    <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
                      <mat-label>SSN</mat-label>
                      <input matInput formControlName="ssn" mask="XXX-XX-XXXX" required [hiddenInput]="ssnMasked" [patterns]="ssnReplacePattern">
                      <img class="ssn-eye" src="icons/ssn-mask.svg" fill="currentColor" matSuffix (click)="toggleSSNVisibility()" matTooltip="Show/Hide SSN">
                      @if (owner.get('ssn')?.hasError('required')) {
                        <mat-error>
                          SSN is required
                        </mat-error>
                      }
                      @if (owner.get('ssn')?.hasError('mask')) {
                        <mat-error>
                          SSN must be in the format XXX-XX-XXXX
                        </mat-error>
                      }
                    </mat-form-field>
                    <!-- Street Address -->
                    <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
                      <mat-label>Street Address</mat-label>
                      <input matInput formControlName="streetAddress" required>
                      @if (owner.get('streetAddress')?.hasError('required')) {
                        <mat-error>
                          Street Address is required
                        </mat-error>
                      }
                    </mat-form-field>
                    <!-- Suite/Unit/Office -->
                    <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
                      <mat-label>Suite/Unit/Office</mat-label>
                      <input matInput formControlName="suiteUnitOffice">
                    </mat-form-field>
                    <!-- City -->
                    <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
                      <mat-label>City</mat-label>
                      <input matInput formControlName="city" required>
                      @if (owner.get('city')?.hasError('required')) {
                        <mat-error>
                          City is required
                        </mat-error>
                      }
                    </mat-form-field>
                    <!-- State -->
                    <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
                      <mat-label>State</mat-label>
                      <mat-select formControlName="state">
                        @for (state of usStates; track state) {
                          <mat-option [value]="state">{{state}}</mat-option>
                        }
                      </mat-select>
                      @if (owner.get('state')?.hasError('required')) {
                        <mat-error>
                          Please enter a valid state abbreviation
                        </mat-error>
                      }
                    </mat-form-field>
                    <!-- Zip Code -->
                    <mat-form-field appearance="outline" class="full-width" hideRequiredMarker>
                      <mat-label>Zip Code</mat-label>
                      <input matInput formControlName="zipCode" required type="text" maxlength="5" pattern="\d{5}"
                        inputmode="numeric" (keypress)="onlyNumberKey($event)" placeholder="Enter 5-digit Zip Code">
                      @if (owner.get('zipCode')?.hasError('required')) {
                        <mat-error>
                          Zip Code is required
                        </mat-error>
                      }
                      @if (owner.get('zipCode')?.hasError('pattern')) {
                        <mat-error>
                          Zip Code must be exactly 5 digits
                        </mat-error>
                      }
                    </mat-form-field>
                  </div>
                  <!-- Remove Owner Button -->
                  <div class="remove-owner-button">
                    <button mat-icon-button color="warn" type="button" (click)="removeOwner(i)" matTooltip="Remove Owner">
                      <mat-icon>remove_circle</mat-icon>
                    </button>
                  </div>
                </mat-expansion-panel>
              }
            </mat-accordion>
          </div>
        </mat-card-content>
      </mat-card>
      <!-- Add Owner Button -->
      <div class="add-owner-button">
        <button mat-raised-button color="primary" type="button" (click)="addOwner()"
          [disabled]="owners.length >= maxOwners" matTooltip="Add Owner">
          <mat-icon>add_circle</mat-icon> Add Owner
        </button>
      </div>
      <!-- Connect Your Bank Section -->
      <mat-card>
        <mat-card-header>
          <mat-card-title>
            <div style="display: flex; align-items: center;">
              Connect Your Bank
              <button
                mat-raised-button
                class="plaid-button"
                color="primary"
                (click)="initPlaidLink()"
                [disabled]="bankConnected">
                Connect Bank via Plaid
              </button>
              <!-- Conditionally display the success message -->
              @if (bankConnected) {
                <span class="success-message" style="margin-left: 10px; font-size: medium; display: flex; align-items: center;">
                  <img src="icons/checkmark.svg" alt="Checkmark" style="width: 40px; height: 40px; margin-right: 5px;">
                  Bank account connected successfully!
                </span>
              }
            </div>
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
        </mat-card-content>
      </mat-card>
    </form>
    <!-- Submit Button -->
    <div class="form-actions">
      <button mat-raised-button color="primary" type="button" (click)="openTosDialog()"
        [disabled]="borrowerForm.invalid || isLoading">
        Submit Application
      </button>
    </div>
    <!-- Terms of Service Modal -->
    <ng-template #tosDialog>
      <div mat-dialog-content class="tos-content">
        <h2>Terms of Service</h2>
        <div class="tos-text" (scroll)="onTosScroll($event)" style="max-height: 300px; overflow: auto;">
          <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa.
          </p>
          <p>Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Donec quam felis,
          ultricies nec.</p>
          <p>Aliquam lorem ante, dapibus in, viverra quis, feugiat a, tellus. Phasellus viverra nulla ut metus varius
          laoreet. Quisque rutrum.</p>
          <p>Aenean imperdiet. Etiam ultricies nisi vel augue. Curabitur ullamcorper ultricies nisi.</p>
          <p>Scroll to the bottom to proceed.</p>
          <!-- Repeat content to ensure scrolling -->
          <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa.
          </p>
          <p>Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Donec quam felis,
          ultricies nec.</p>
          <p>Aliquam lorem ante, dapibus in, viverra quis, feugiat a, tellus. Phasellus viverra nulla ut metus varius
          laoreet. Quisque rutrum.</p>
          <p>Aenean imperdiet. Etiam ultricies nisi vel augue. Curabitur ullamcorper ultricies nisi.</p>
          <p>Scroll to the bottom to proceed.</p>
        </div>
        <mat-checkbox [formControl]="tosAcceptedControl">
          I accept the Terms of Service
        </mat-checkbox>
      </div>
      <div mat-dialog-actions>
        <button mat-button mat-dialog-close>Cancel</button>
        <button mat-raised-button color="primary" [disabled]="!tosAcceptedControl.value || !hasScrolledToBottom"
          (click)="confirmTos()">
          OK
        </button>
      </div>
    </ng-template>
  </div>
}

<!-- Error Message -->
@if (!isLoading && errorMessage) {
  <div class="error-message">
    <p>{{ errorMessage }}</p>
  </div>
}